<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisVersionDiffPanel from 'dashboard/components-next/LaisVersionDiffPanel.vue';
import LaisActionReasonModal from 'dashboard/components-next/LaisActionReasonModal.vue';
import { getOfferStatusLabel } from 'dashboard/helper/lais/offerPresenter';

/**
 * OfferCounterModal — counter-offer (contraproposta) flow for a rent/sale
 * offer. It edits the proposed revision values (valor, validade, garantia,
 * condições), shows a live DIFF against the current revision and confirms via a
 * required reason. It never talks to the API directly: it emits `submit` with a
 * payload shaped exactly for `PropertyOffersAPI.counter(id, payload)` and the
 * parent owns loading/success/error/conflict. The optimistic-concurrency guard
 * (`expected_revision_id`) is surfaced as a distinct CONFLICT state driven by
 * the `conflict` prop — the parent flips it on a backend 409 and the operator is
 * asked to reload, never silently overwrite.
 */
const props = defineProps({
  show: { type: Boolean, default: false },
  offer: { type: Object, default: null },
  // Current revision id used for the optimistic-lock check on the backend.
  expectedRevisionId: { type: [Number, String], default: null },
  isSubmitting: { type: Boolean, default: false },
  // Set by the parent when the backend rejects the counter with a stale
  // revision (HTTP 409). Renders the conflict banner and blocks re-submitting.
  conflict: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit', 'reload']);

const { t } = useI18n();

const STATUS_TONE = Object.freeze({
  submitted: 'info',
  accepted: 'positive',
  declined: 'danger',
  withdrawn: 'neutral',
});

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});
// Decorative input affordance (the input has an aria-label), kept out of raw
// template text so the i18n bare-string lint stays clean.
const currencySymbol = 'R$';

const amount = ref('');
const expiresAt = ref('');
const guarantee = ref('');
const conditions = ref('');
const showReasonModal = ref(false);

const currentRevision = computed(() => props.offer?.current_revision || {});

const beforeAmount = computed(
  () => currentRevision.value.offer_amount ?? props.offer?.offer_amount ?? null
);
const beforeExpires = computed(() => currentRevision.value.expires_at || '');
const beforeGuarantee = computed(
  () => currentRevision.value.terms?.guarantee || ''
);
const beforeConditions = computed(
  () => currentRevision.value.terms?.conditions || ''
);

const resetForm = () => {
  amount.value =
    beforeAmount.value === null || beforeAmount.value === undefined
      ? ''
      : String(beforeAmount.value);
  expiresAt.value = String(beforeExpires.value || '').slice(0, 10);
  guarantee.value = '';
  conditions.value = '';
  showReasonModal.value = false;
};

watch(
  () => props.show,
  isOpen => {
    if (isOpen) resetForm();
  },
  { immediate: true }
);

// When the parent flags a concurrency conflict, drop back to the form so the
// operator sees the banner and reloads instead of blindly re-confirming.
watch(
  () => props.conflict,
  hasConflict => {
    if (hasConflict) showReasonModal.value = false;
  }
);

const amountNumber = computed(() => {
  const parsed = Number(amount.value);
  return Number.isFinite(parsed) ? parsed : 0;
});

const isAmountValid = computed(() => amountNumber.value > 0);

const formatCurrency = value =>
  value === null || value === undefined || value === ''
    ? ''
    : currencyFormatter.format(Number(value));

const formatDate = value => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return String(value);
};

const statusLabel = computed(() =>
  props.offer?.status ? getOfferStatusLabel(props.offer.status, { t }) : ''
);
const statusTone = computed(
  () => STATUS_TONE[props.offer?.status] || 'neutral'
);

const diffFields = computed(() => {
  const fields = [
    {
      key: 'offer_amount',
      label: t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALUE'),
      before: formatCurrency(beforeAmount.value),
      after: formatCurrency(isAmountValid.value ? amountNumber.value : ''),
    },
  ];
  if (beforeExpires.value || expiresAt.value) {
    fields.push({
      key: 'expires_at',
      label: t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALIDITY'),
      before: formatDate(beforeExpires.value),
      after: formatDate(expiresAt.value),
    });
  }
  if (beforeGuarantee.value || guarantee.value.trim()) {
    fields.push({
      key: 'guarantee',
      label: t('PROPERTIES.OFFERS.COUNTER.FIELDS.GUARANTEE'),
      before: beforeGuarantee.value,
      after: guarantee.value.trim(),
    });
  }
  if (beforeConditions.value || conditions.value.trim()) {
    fields.push({
      key: 'conditions',
      label: t('PROPERTIES.OFFERS.COUNTER.FIELDS.CONDITIONS'),
      before: beforeConditions.value,
      after: conditions.value.trim(),
    });
  }
  return fields;
});

const canReview = computed(
  () => isAmountValid.value && !props.isSubmitting && !props.conflict
);

const buildRevision = () => {
  const revision = { offer_amount: amountNumber.value };
  if (expiresAt.value) revision.expires_at = expiresAt.value;
  const terms = {};
  if (guarantee.value.trim()) terms.guarantee = guarantee.value.trim();
  if (conditions.value.trim()) terms.conditions = conditions.value.trim();
  if (Object.keys(terms).length) revision.terms = terms;
  return revision;
};

const handleClose = () => {
  if (props.isSubmitting) return;
  emit('close');
};

const openReasonModal = () => {
  if (!canReview.value) return;
  showReasonModal.value = true;
};

const handleConfirm = ({ reason }) => {
  emit('submit', {
    property_offer_revision: buildRevision(),
    expected_revision_id: props.expectedRevisionId,
    notes: reason,
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('PROPERTIES.OFFERS.COUNTER.TITLE')"
    max-width-class="max-w-[680px]"
    @close="handleClose"
  >
    <div v-if="offer" class="flex flex-col gap-5">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <p class="max-w-[420px] text-lais-body-sm text-lais-text-body">
          {{ t('PROPERTIES.OFFERS.COUNTER.DESCRIPTION') }}
        </p>
        <div class="flex flex-col items-end gap-1">
          <span class="text-lais-label-xs uppercase text-lais-text-muted">
            {{ t('PROPERTIES.OFFERS.COUNTER.CURRENT_STATUS') }}
          </span>
          <LaisStatusBadge
            v-if="statusLabel"
            :label="statusLabel"
            :tone="statusTone"
          />
        </div>
      </div>

      <div
        v-if="conflict"
        role="alert"
        class="flex flex-col gap-2 rounded-lais border border-lais-danger/40 bg-lais-danger-50 px-4 py-3"
      >
        <div class="flex items-center gap-2">
          <LaisStatusBadge
            :label="t('PROPERTIES.OFFERS.COUNTER.CONFLICT.BADGE')"
            tone="danger"
            icon="i-lucide-triangle-alert"
            size="sm"
          />
          <p class="text-lais-label-sm font-semibold text-lais-danger">
            {{ t('PROPERTIES.OFFERS.COUNTER.CONFLICT.TITLE') }}
          </p>
        </div>
        <p class="text-lais-body-sm text-lais-text-body">
          {{ t('PROPERTIES.OFFERS.COUNTER.CONFLICT.MESSAGE') }}
        </p>
        <div>
          <LaisPrimaryButton
            variant="ghost"
            size="sm"
            icon="i-lucide-refresh-ccw"
            @click="emit('reload')"
          >
            {{ t('PROPERTIES.OFFERS.COUNTER.CONFLICT.RELOAD') }}
          </LaisPrimaryButton>
        </div>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALUE') }}
          </span>
          <div class="relative">
            <span
              class="pointer-events-none absolute inset-y-0 left-3 grid place-items-center text-lais-body-sm text-lais-text-muted"
              aria-hidden="true"
            >
              {{ currencySymbol }}
            </span>
            <input
              v-model="amount"
              type="number"
              min="0"
              step="1"
              inputmode="numeric"
              :disabled="isSubmitting"
              :aria-label="t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALUE')"
              class="min-h-10 w-full rounded-lais border border-lais-border bg-lais-bg-card py-2 pl-10 pr-3 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus disabled:opacity-60"
            />
          </div>
          <span class="text-lais-body-xs text-lais-text-helper">
            {{ t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALUE_HELPER') }}
          </span>
        </label>

        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALIDITY') }}
          </span>
          <input
            v-model="expiresAt"
            type="date"
            :disabled="isSubmitting"
            :aria-label="t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALIDITY')"
            class="min-h-10 w-full rounded-lais border border-lais-border bg-lais-bg-card px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus disabled:opacity-60"
          />
          <span class="text-lais-body-xs text-lais-text-helper">
            {{ t('PROPERTIES.OFFERS.COUNTER.FIELDS.VALIDITY_HELPER') }}
          </span>
        </label>

        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('PROPERTIES.OFFERS.COUNTER.FIELDS.GUARANTEE') }}
          </span>
          <input
            v-model="guarantee"
            type="text"
            :disabled="isSubmitting"
            :placeholder="
              t('PROPERTIES.OFFERS.COUNTER.FIELDS.GUARANTEE_PLACEHOLDER')
            "
            class="min-h-10 w-full rounded-lais border border-lais-border bg-lais-bg-card px-3 py-2 text-lais-body-sm text-lais-text placeholder:text-lais-text-muted focus-visible:outline-none focus-visible:shadow-lais-focus disabled:opacity-60"
          />
        </label>

        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('PROPERTIES.OFFERS.COUNTER.FIELDS.CONDITIONS') }}
          </span>
          <input
            v-model="conditions"
            type="text"
            :disabled="isSubmitting"
            :placeholder="
              t('PROPERTIES.OFFERS.COUNTER.FIELDS.CONDITIONS_PLACEHOLDER')
            "
            class="min-h-10 w-full rounded-lais border border-lais-border bg-lais-bg-card px-3 py-2 text-lais-body-sm text-lais-text placeholder:text-lais-text-muted focus-visible:outline-none focus-visible:shadow-lais-focus disabled:opacity-60"
          />
        </label>
      </div>

      <div class="grid gap-2">
        <p
          class="text-lais-label-xs font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          {{ t('PROPERTIES.OFFERS.COUNTER.DIFF_TITLE') }}
        </p>
        <LaisVersionDiffPanel
          :fields="diffFields"
          :before-label="t('PROPERTIES.OFFERS.COUNTER.BEFORE_LABEL')"
          :after-label="t('PROPERTIES.OFFERS.COUNTER.AFTER_LABEL')"
          :changed-label="t('PROPERTIES.OFFERS.COUNTER.FIELDS.CHANGED')"
          :empty-label="t('PROPERTIES.OFFERS.COUNTER.EMPTY_DIFF')"
        />
      </div>

      <p v-if="!isAmountValid" class="text-lais-body-xs text-lais-danger">
        {{ t('PROPERTIES.OFFERS.COUNTER.VALUE_REQUIRED') }}
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton
          variant="ghost"
          :disabled="isSubmitting"
          @click="handleClose"
        >
          {{ t('PROPERTIES.OFFERS.COUNTER.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-git-compare"
          :disabled="!canReview"
          :is-loading="isSubmitting"
          @click="openReasonModal"
        >
          {{ t('PROPERTIES.OFFERS.COUNTER.REVIEW') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisActionReasonModal
    :show="showReasonModal"
    :title="t('PROPERTIES.OFFERS.COUNTER.CONFIRM.TITLE')"
    :description="t('PROPERTIES.OFFERS.COUNTER.CONFIRM.DESCRIPTION')"
    :consequence="t('PROPERTIES.OFFERS.COUNTER.CONFIRM.CONSEQUENCE')"
    :reason-label="t('PROPERTIES.OFFERS.COUNTER.CONFIRM.REASON_LABEL')"
    :reason-placeholder="
      t('PROPERTIES.OFFERS.COUNTER.CONFIRM.REASON_PLACEHOLDER')
    "
    :acknowledge-label="t('PROPERTIES.OFFERS.COUNTER.CONFIRM.ACKNOWLEDGE')"
    :confirm-text="t('PROPERTIES.OFFERS.COUNTER.CONFIRM.CONFIRM')"
    confirm-variant="primary"
    :is-loading="isSubmitting"
    @confirm="handleConfirm"
    @close="showReasonModal = false"
  />
</template>
